import type { Language } from '@eformulieren/engine'
import {
  AdviesAnkerSoort,
  AdviesTermijn,
  AdviesToestand,
  AdviesVerzoek,
  DelingRule,
  AdminOpening,
  AdminOpeningForExternal,
  NormalizedEmail,
  Side,
  normalizeEmail,
  type ProjectionSection,
  type Projection,
} from '@eformulieren/model'
import type { AdviceLine, Bilingual, InviteRequest, OwnGrant, ShareLine, ShareNotice, ShareSection, OpeningEntry, WriteBlock } from '@eformulieren/renderer'
import { z } from 'zod'
import { NO_CONNECTION } from './rejection.js'

/**
 * Wat de pagina bij delen zelf beslist (R-64, R-65, R-65a, R-65b, R-65c, R-66, R-66a, R-140,
 * ontwerp 6.4 en 9.2).
 *
 * **Vóór het verzenden: alleen aanbieden wat de deler mag geven.** De pod weigert wat te ver gaat
 * (`apps/runtime/src/delen.ts`), en de opslagdienst nog eens. Een scherm dat bewerken aanbiedt en
 * het daarna weigert, laat de deler raden wat hij fout deed; daarom biedt de pagina per sectie
 * alleen bewerken aan waar de pod het zou toelaten. Dat is geen tweede definitie van de regel maar
 * een weergave van de projectie die de pod zelf leverde: het recht per sectie, het sectietype en
 * de rol. Eén verschil blijft: de pod toetst R-65a tegen de projectie over alleen de toekenningen
 * van het processysteem van de deler (`getSharersProjection`), en de pagina kent alleen de volle
 * projectie. Wie naast een eigen toekenning ook via een deling bewerkt, krijgt hier dus soms een
 * keuze aangeboden die de pod weigert; die weigering komt dan als zin met de secties bij naam.
 *
 * **Na een weigering: een zin, en de secties bij naam.** De codes komen uit de pod en uit de
 * opslagdienst (`PASS_THROUGH_ON_SHARE` in `storeclient.ts`); de secties staan bovenaan in het lijf
 * (`secties`). De zin noemt ze met hun titel en de melding hoort programmatisch bij die secties
 * (R-140); het scherm koppelt haar daar met `aria-describedby`. Een code die hier niet staat
 * krijgt de algemene zin: een nieuwe weigering in de pod breekt de pagina niet.
 */

/**
 * Per zichtbare sectie wat er te geven valt (R-65a, R-65b, R-65c), met de reden als schrijven niet kan.
 *
 * Bewerken alleen in een gewone sectie waar de deler zelf bewerkt, en nooit door wie als toetser
 * handelt. Een toetssectie geeft uitsluitend lezen (R-65b), een bronsectie schrijft niemand
 * (R-56), en de rol `toetser` in de projectie betekent een toekenning als toetser op dit dossier
 * en blok, en dan geeft hij nergens bewerkrecht (R-65c). Of de beurt bewerken toestaat zit al in
 * het recht per sectie: de projectie weegt de beurt mee (R-193). De reden staat bij de sectie in
 * het scherm en niet als fout achteraf (besluit 1); de eerste die geldt, in de volgorde van de pod.
 *
 * Het nummer is de plaats in de definitie (`nummer` van de pod, G-17), zoals de inhoudsopgave het toont. `verborgen` komt van het
 * formulier (de zichtbaarheid die de renderer uitrekent, G-10) en `uitgesloten` uit `GET /advies`.
 */
export function shareOffer(
  projection: Pick<Projection, 'rol'> & {
    readonly secties: readonly (Pick<ProjectionSection, 'id' | 'type' | 'recht' | 'titel'> & { readonly nummer?: number | undefined })[]
  },
  language: Language,
  extra: { readonly verborgen?: ReadonlySet<string>; readonly uitgesloten?: readonly string[] } = {},
): readonly ShareSection[] {
  const excluded = new Set(extra.uitgesloten ?? [])
  return projection.secties.map((section, index) => {
    const reason: WriteBlock | undefined =
      section.type === 'toets'
        ? 'toetssectie'
        : section.type === 'bron'
          ? 'bronsectie'
          : projection.rol === 'toetser'
            ? 'toetser'
            : section.recht !== 'bewerken'
              ? 'zelf-lezen'
              : undefined
    return {
      id: section.id,
      titel: section.titel[language],
      nummer: section.nummer ?? index + 1,
      magBewerken: reason === undefined,
      ...(reason === undefined ? {} : { schrijfReden: reason }),
      ...(extra.verborgen?.has(section.id) === true ? { verborgen: true } : {}),
      ...(excluded.has(section.id) ? { adviesUitgesloten: true } : {}),
    }
  })
}

/** De delen van een weigering waar de pagina iets mee doet. */
export interface ShareRejection {
  readonly code?: string
  readonly secties: readonly string[]
  readonly problemen: readonly string[]
}

const RejectionBody = z.object({
  code: z.string().optional(),
  secties: z.array(z.string()).optional(),
  problemen: z.array(z.string()).optional(),
})

/**
 * Code, secties en problemen uit een weigering (`{ ...velden, code, melding, problemen }`,
 * `server.ts`). Een lijf dat geen JSON is of de vorm niet heeft, levert niets op en geen gok.
 */
export function rejectionFromBody(body: string): ShareRejection {
  try {
    const read = RejectionBody.safeParse(JSON.parse(body))
    if (!read.success) return { secties: [], problemen: [] }
    return {
      ...(read.data.code === undefined ? {} : { code: read.data.code }),
      secties: read.data.secties ?? [],
      problemen: read.data.problemen ?? [],
    }
  } catch {
    return { secties: [], problemen: [] }
  }
}

/** Zinnen met de secties erin; `{secties}` wordt de lijst titels. */
const WITH_SECTIONS: Readonly<Record<string, Bilingual>> = {
  'meer-dan-de-deler': {
    nl: 'U kunt niet meer geven dan u zelf op dit moment hebt. Dat geldt hier voor: {secties}.',
    en: 'You cannot give more than you have yourself at this moment. This applies to: {secties}.',
  },
  'toetssectie-alleen-lezen': {
    nl: 'Een toetssectie kunt u alleen om te lezen delen. Dat geldt hier voor: {secties}.',
    en: 'A review section can only be shared for reading. This applies to: {secties}.',
  },
  'bronsectie-alleen-lezen': {
    nl: 'Deze secties tonen gegevens uit een eerder formulier en kunt u alleen om te lezen delen: {secties}.',
    en: 'These sections show data from an earlier form and can only be shared for reading: {secties}.',
  },
  'toetser-geeft-geen-bewerkrecht': {
    nl: 'Omdat u dit formulier toetst, kunt u alleen om te lezen delen. Kies lezen bij: {secties}.',
    en: 'Because you review this form, you can only share for reading. Choose read at: {secties}.',
  },
  'sectie-uitgesloten-van-advies': {
    nl: 'Deze secties kunt u in dit formulier niet voor advies delen: {secties}.',
    en: 'In this form you cannot share these sections for advice: {secties}.',
  },
  'onbekende-sectie': {
    nl: 'Deze secties horen niet (meer) bij dit formulier: {secties}. Ververs de pagina en probeer het opnieuw.',
    en: 'These sections are not (or no longer) part of this form: {secties}. Refresh the page and try again.',
  },
}

const WITHOUT_SECTIONS: Readonly<Record<string, Bilingual>> = {
  'mag-niet-delen': {
    nl: 'U kunt dit formulier niet delen. Delen kan wie het formulier van NWO toegewezen kreeg, niet wie het zelf via een uitnodiging opende.',
    en: 'You cannot share this form. Sharing is for those to whom NWO assigned the form, not for those who opened it through an invitation.',
  },
  'deler-zonder-toekenning': {
    nl: 'U kunt dit formulier niet (meer) delen: uw toegang komt niet van NWO zelf.',
    en: 'You can no longer share this form: your access does not come from NWO itself.',
  },
  'deler-kant-wijkt-af': {
    nl: 'Delen lukt niet met de manier waarop u nu bent ingelogd. Log in zoals u dit formulier gewoonlijk opent en probeer het opnieuw.',
    en: 'Sharing does not work with the way you are signed in now. Sign in the way you usually open this form and try again.',
  },
  'instantie-is-bevroren': {
    nl: 'Dit formulier is afgesloten; delen kan niet meer.',
    en: 'This form has been closed; it can no longer be shared.',
  },
  'beurt-staat-op-toets': {
    nl: 'Het formulier wordt nu getoetst; in deze fase kunt u niets om te bewerken delen.',
    en: 'The form is being reviewed now; in this phase you cannot share anything for editing.',
  },
  'publicatie-zonder-adres': {
    nl: 'Voor dit formulier kan nog geen uitnodiging worden verstuurd. Neem contact op met NWO.',
    en: 'No invitation can be sent for this form yet. Please contact NWO.',
  },
  'niet-de-deler': {
    nl: 'Dit kunt u alleen bij wat u zelf deelde, of wat de aanvrager of een gemachtigde deelde als u een van beiden bent.',
    en: 'You can only do this for what you shared yourself, or what the applicant or an authorised representative shared if you are one of them.',
  },
  'uitnodiging-is-afgehandeld': {
    nl: 'Deze uitnodiging is al afgehandeld; er valt niets meer aan te veranderen.',
    en: 'This invitation has already been dealt with; nothing can be changed any more.',
  },
  'rechten-van-advies': {
    nl: 'Wat een adviseur leest, staat op de adviesvraag en wijzigt u hier niet. Trek de vraag in en stel een nieuwe.',
    en: 'What an adviser reads is part of the request for advice and cannot be changed here. Withdraw the request and ask a new one.',
  },
  'advies-staat-uit': {
    nl: 'Advies vragen staat voor dit formulier uit.',
    en: 'Asking for advice is switched off for this form.',
  },
  'advies-is-afgehandeld': {
    nl: 'Dit adviesverzoek is al beëindigd; er valt niets meer aan te doen. Ververs de pagina om de stand te zien.',
    en: 'This request for advice has already ended; nothing more can be done. Refresh the page to see where it stands.',
  },
  'al-verklaard': {
    nl: 'U hebt uw belangenverklaring al gegeven. Ververs de pagina.',
    en: 'You have already given your declaration of interest. Refresh the page.',
  },
  'nog-geen-verklaring': {
    nl: 'De adviseur heeft nog geen belangenverklaring gegeven; daarna kunt u haar beoordelen.',
    en: 'The adviser has not given a declaration of interest yet; you can review it after that.',
  },
  'beurt-gesloten': {
    nl: 'De fase waarin om advies is gevraagd, is afgesloten. Het verzoek kan niet meer doorgaan; wel belang vastleggen kan nog.',
    en: 'The phase in which advice was asked has been closed. The request can no longer continue; recording an interest is still possible.',
  },
  'vrager-zonder-toekenning': {
    nl: 'Wie om advies vroeg, heeft zelf geen toegang meer tot dit formulier. Het verzoek kan niet meer doorgaan.',
    en: 'Whoever asked for advice no longer has access to this form. The request can no longer continue.',
  },
  'beheer-alleen-oordeel': {
    nl: 'Als beheerder beoordeelt u alleen de belangenverklaring.',
    en: 'As an administrator you only review the declaration of interest.',
  },
  'eerst-belangenverklaring': {
    nl: 'Geef eerst uw belangenverklaring; daarna kunt u antwoorden.',
    en: 'Give your declaration of interest first; then you can answer.',
  },
  'alleen-de-interne-kant': {
    nl: 'Dit kan alleen een medewerker van NWO.',
    en: 'Only an NWO employee can do this.',
  },
  'anker-ongeldig': {
    nl: 'Waar de vraag over gaat, hoort bij wat u deelt. Kies bij Wat u deelt ook de sectie van het onderwerp.',
    en: 'What the question is about must be part of what you share. Under What you share, also choose the section of the subject.',
  },
  'rol-vraagt-geen-advies': {
    nl: 'In dit formulier vraagt u in uw rol geen advies.',
    en: 'In this form your role does not ask for advice.',
  },
  'niet-gevonden': {
    nl: 'Dit formulier is niet (meer) voor u beschikbaar. Ververs de pagina.',
    en: 'This form is not (or no longer) available to you. Refresh the page.',
  },
  'te-veel-verzoeken': {
    nl: 'Er komen even te veel verzoeken binnen. Probeer het over een minuut opnieuw.',
    en: 'Too many requests at the moment. Please try again in a minute.',
  },
  'geen-geldig-token': {
    nl: 'Uw sessie is verlopen. Log opnieuw in en probeer het daarna nog eens.',
    en: 'Your session has expired. Sign in again and then try once more.',
  },
  [NO_CONNECTION]: {
    nl: 'De verbinding lukte niet. Probeer het opnieuw.',
    en: 'The connection failed. Please try again.',
  },
}

const ONLY_INSIDE: Bilingual = {
  nl: 'In dit formulier staat een adviseur binnen NWO. Kies Binnen NWO.',
  en: 'In this form an adviser is within NWO. Choose Within NWO.',
}

const EMPTY_QUESTION: Bilingual = {
  nl: 'Schrijf uw vraag aan de adviseur.',
  en: 'Write your question for the adviser.',
}

const INVALID_ADDRESS: Bilingual = {
  nl: 'Vul een geldig e-mailadres in, zoals naam@voorbeeld.nl.',
  en: 'Enter a valid email address, such as name@example.org.',
}

const GENERAL: Bilingual = {
  nl: 'Delen is niet gelukt. Probeer het opnieuw.',
  en: 'Sharing did not work. Please try again.',
}

/**
 * De zin bij een weigering van de pod, in de taal van de lezer (R-143, R-144).
 *
 * De ruwe keten hoort in de console en nooit hier: naar het scherm gaat geen code en geen JSON
 * (ontwerp 11.4).
 */
export function shareNotice(
  rejection: ShareRejection,
  language: Language,
  sectionName: (sectionId: string) => string | undefined,
): ShareNotice {
  const code = rejection.code
  if (code !== undefined) {
    const withSections = WITH_SECTIONS[code]
    if (withSections !== undefined) {
      // Een sectie die de kijker niet heeft, heeft hier geen naam: geen id in beeld.
      const names = rejection.secties.flatMap((id) => sectionName(id) ?? []).join(', ')
      return { tekst: withSections[language].replace('{secties}', names), secties: [...rejection.secties] }
    }
    const without = WITHOUT_SECTIONS[code]
    if (without !== undefined) return { tekst: without[language], secties: [] }
    if (code === 'adviseur-alleen-binnen-nwo') return { tekst: ONLY_INSIDE[language], secties: [], veld: 'adviseurKant' }
    // Een validatiefout van de pod. Alleen het adres kan de deler zelf herstellen; de rechten
    // komen uit dit scherm en zijn dan een fout van de pagina, niet van hem.
    if (code === 'ongeldig-verzoek' && rejection.problemen.some((problem) => problem.startsWith('email'))) {
      return { tekst: INVALID_ADDRESS[language], secties: [], veld: 'email' }
    }
    if (code === 'ongeldig-verzoek' && rejection.problemen.some((problem) => problem.startsWith('vraag'))) {
      return { tekst: EMPTY_QUESTION[language], secties: [], veld: 'vraag' }
    }
  }
  return { tekst: GENERAL[language], secties: [] }
}

/**
 * Een regel uit het overzicht van de pod: een `Delingsregel`, het adres alleen als de kijker het mag
 * zien, en wat de pod er voor deze kijker bij zette (besluiten 2, 5 en 6 van 30 oktober 2026,
 * `apps/runtime/src/overview.ts`): of hij hem zelf deelde, wie uitnodigde als rol, en of hij mag
 * intrekken en rechten wijzigen. Verder strikt: een veld dat hier niet staat, hoort niet in beeld.
 */
export const OverviewEntry = DelingRule.omit({ email: true }).extend({
  email: NormalizedEmail.optional(),
  eigen: z.boolean(),
  delerRol: z.enum(['aanvrager', 'gemachtigde', 'medewerker-nwo', 'u']),
  intrekbaar: z.boolean(),
  wijzigbaar: z.boolean(),
})
export type OverviewEntry = z.infer<typeof OverviewEntry>

/**
 * Wat de pagina van een adviesverzoek leest, voor de regel in de lijst en de dialoog.
 *
 * Uit `GET /advies` niet strikt, met opzet: daar stuurt de pod het verzoek aan de kijker aangepast
 * (`mijnRol`, en aan de externe kant het merkteken van NWO in plaats van een sleutel, `forViewer` in
 * `advies.ts`). Het scherm gebruikt alleen dit deel, en de rest (de vraag, het gesprek, de
 * persoonssleutels) gaat niet verder dan dit bestand.
 */
const AdviceSummary = z.object({
  id: z.string().min(1),
  uitnodigingId: z.string().min(1),
  toestand: AdviesToestand,
  anker: z.object({ soort: AdviesAnkerSoort, label: z.string().min(1) }),
  termijn: AdviesTermijn.nullable(),
  adviseurKant: Side,
  gedeeld: z.array(z.string()),
})

/** Een toekenning van de eigen kant als regel, met "Wijzigen via NWO" (besluit 7). */
export const OwnSideGrant = z.strictObject({
  rol: z.enum(['aanvrager', 'gemachtigde', 'invuller', 'toetser']),
  isU: z.boolean(),
})

/** Een label per persoon voor aanwezigheid en opmerkingen: een rol, en het adres waar dat mag. */
export const PersonLabel = z.strictObject({
  rol: z.enum(['aanvrager', 'gemachtigde', 'medeschrijver', 'meelezer', 'medewerker-nwo', 'adviseur']),
  email: z.string().optional(),
})
export type PersonLabel = z.infer<typeof PersonLabel>

const ROLE_NAME: Readonly<Record<PersonLabel['rol'], Bilingual>> = {
  aanvrager: { nl: 'De aanvrager', en: 'The applicant' },
  gemachtigde: { nl: 'De gemachtigde', en: 'The authorised representative' },
  medeschrijver: { nl: 'Medeschrijver', en: 'Co-author' },
  meelezer: { nl: 'Meelezer', en: 'Reader' },
  'medewerker-nwo': { nl: 'Medewerker van NWO', en: 'NWO staff member' },
  adviseur: { nl: 'Adviseur', en: 'Adviser' },
}

/** Wie de pod (nog) niet kent: geen sleutel en geen verzonnen nummer in beeld (G-19). */
const UNKNOWN_PERSON: Bilingual = { nl: 'Een andere deelnemer', en: 'Another participant' }

/**
 * Hoe een persoon heet in de aanwezigheid en bij een opmerking (audit delen G-05, G-19, analyse 4.6,
 * R-154, besluit 5 van 30 oktober 2026).
 *
 * Uit `labels` van het overzicht: het adres als de pod het voor deze kijker geeft (wie hij of zijn
 * partij uitnodigde), anders de rol in de taal van de lezer. Een sleutel die er niet in staat (iemand
 * die net binnenkwam) heet neutraal; de pagina leest het overzicht dan opnieuw.
 */
export function personNameFrom(labels: Readonly<Record<string, PersonLabel>>, language: Language): (person: string) => string {
  return (person) => {
    const label = labels[person]
    if (label === undefined) return UNKNOWN_PERSON[language]
    return label.email ?? ROLE_NAME[label.rol][language]
  }
}

const Overview = z.strictObject({
  /**
   * Aan de interne kant bij een definitie met advies: de adviesverzoeken naast de regels (fase 1,
   * `sharing.ts` in de pod), strikt zoals de opslag ze bewaart. Zonder deze sleutel viel het hele
   * overzicht van de interne kant weg (voor-opnamen 20 tot 22).
   */
  adviesverzoeken: z.array(AdviesVerzoek).optional(),
  delingen: z.array(OverviewEntry),
  /** De openingen door beheer (R-66b, opdracht 6): volledig aan de interne kant, smal aan de externe. */
  openingenDoorBeheer: z.array(z.union([AdminOpening, AdminOpeningForExternal])),
  /** Hoe de kijker binnenkwam (besluit 6, 7). */
  mijnToegang: z.enum(['processysteem', 'deling', 'beheer']),
  /** De toekenningen van de eigen kant, alleen met hun rol (besluit 7). */
  toekenningen: z.array(OwnSideGrant),
  /** Voor aanwezigheid en opmerkingen; dit scherm gebruikt ze niet. */
  labels: z.record(z.string(), PersonLabel),
})

/** Het overzicht zoals het scherm het krijgt: zonder persoonssleutels. */
export interface ReadOverview {
  readonly delingen: readonly ShareLine[]
  readonly openingen: readonly OpeningEntry[]
  readonly toekenningen: readonly OwnGrant[]
  readonly advies: readonly AdviceLine[]
  readonly mijnToegang: 'processysteem' | 'deling' | 'beheer'
  readonly labels: Readonly<Record<string, PersonLabel>>
}

/** Een regel voor het scherm: de persoonssleutel van de deler en de instantie blijven hier. */
function lineForScreen(entry: OverviewEntry): ShareLine {
  return {
    id: entry.id,
    ...(entry.email === undefined ? {} : { email: entry.email }),
    rechten: entry.rechten,
    delerKant: entry.delerKant,
    stand: entry.stand,
    aangemaaktOp: entry.aangemaaktOp,
    geldigTot: entry.geldigTot,
    sinds: entry.sinds,
    eigen: entry.eigen,
    delerRol: entry.delerRol,
    intrekbaar: entry.intrekbaar,
    wijzigbaar: entry.wijzigbaar,
  }
}

function adviceForScreen(summary: z.infer<typeof AdviceSummary> | AdviesVerzoek): AdviceLine {
  return {
    id: summary.id,
    uitnodigingId: summary.uitnodigingId,
    toestand: summary.toestand,
    anker: { soort: summary.anker.soort, label: summary.anker.label },
    termijn: summary.termijn,
    adviseurKant: summary.adviseurKant,
    gedeeld: summary.gedeeld,
  }
}

/**
 * Het overzicht uit `GET /delingen` (R-66, R-66a, R-66b), of niets als het niet op de vorm past.
 *
 * Strikt op het hoogste niveau en per regel, net als de pod zelf de regels van de opslagdienst
 * leest: een regel met een veld erbij, een code bijvoorbeeld, hoort hier niet te staan en wordt niet
 * getoond. Pagina en pod komen uit hetzelfde image (R-101), dus de openingen staan er altijd bij,
 * ook als lege lijst.
 */
export function readOverview(body: unknown): ReadOverview | undefined {
  const read = Overview.safeParse(body)
  if (!read.success) return undefined
  return {
    delingen: read.data.delingen.map(lineForScreen),
    openingen: read.data.openingenDoorBeheer,
    toekenningen: read.data.toekenningen,
    advies: (read.data.adviesverzoeken ?? []).map(adviceForScreen),
    mijnToegang: read.data.mijnToegang,
    labels: read.data.labels,
  }
}

const AdviceRead = z.object({
  verzoeken: z.array(AdviceSummary),
  kanVragen: z.boolean(),
  adviseurBuitenNwo: z.boolean().optional(),
  uitgeslotenSecties: z.array(z.string()).optional(),
})

export interface ReadAdvice {
  readonly kanVragen: boolean
  readonly buitenNwo: boolean
  readonly uitgesloten: readonly string[]
  readonly verzoeken: readonly AdviceLine[]
}

/**
 * Wat `GET /advies` zegt (R-184, ontwerp 7.4a): of de kijker kan vragen, en wat de definitie
 * toelaat. Zonder instelling geen buiten NWO en niets uitgesloten: de pod stuurt ze alleen aan wie
 * kan vragen, en de dialoog toont dan geen advies.
 */
export function readAdvice(body: unknown): ReadAdvice | undefined {
  const read = AdviceRead.safeParse(body)
  if (!read.success) return undefined
  return {
    kanVragen: read.data.kanVragen,
    buitenNwo: read.data.adviseurBuitenNwo ?? false,
    uitgesloten: read.data.uitgeslotenSecties ?? [],
    verzoeken: read.data.verzoeken.map(adviceForScreen),
  }
}

/**
 * Het lichaam van `POST /advies` (`AskRequest` in de pod). Het adres genormaliseerd zoals de
 * opslagdienst het bewaart; het label van een veld blijft hier, want de opslag zet het anker-label
 * zelf uit de bevroren definitie.
 */
export function adviceBody(instantieId: string, request: Extract<InviteRequest, { readonly soort: 'advies' }>): Readonly<Record<string, unknown>> {
  const anchor = request.anker
  return {
    instantie: instantieId,
    email: normalizeEmail(request.email),
    adviseurKant: request.adviseurKant,
    anker:
      anchor.soort === 'formulier'
        ? { soort: 'formulier' }
        : anchor.soort === 'sectie'
          ? { soort: 'sectie', sectieId: anchor.sectieId }
          : { soort: anchor.criterium === true ? 'criterium' : 'veld', sectieId: anchor.sectieId, veldId: anchor.veldId },
    gedeeld: [...request.gedeeld],
    termijn: request.termijn,
    vraag: request.vraag.trim(),
  }
}

const BUTTON: Readonly<Record<'delen' | 'delenAdvies' | 'wieToegang', Bilingual>> = {
  delen: { nl: 'Delen', en: 'Share' },
  delenAdvies: { nl: 'Delen en advies vragen', en: 'Share and ask for advice' },
  wieToegang: { nl: 'Wie heeft toegang', en: 'Who has access' },
}

/**
 * Wat de knop in de kop zegt (besluit 1 van 30 oktober 2026, ontwerp 6.4). "Wie heeft toegang" voor
 * wie niet mag delen (R-64d, R-65d); "Delen en advies vragen" alleen aan de interne kant en alleen als
 * `GET /advies` zegt dat de kijker kan vragen; anders "Delen". De externe kant hoort nooit van advies
 * (R-184b), ook niet als de pod zich zou vergissen.
 */
export function shareButtonLabel(projection: Pick<Projection, 'kant' | 'magDelen'>, canAsk: boolean, language: Language): string {
  if (!projection.magDelen) return BUTTON.wieToegang[language]
  return projection.kant === 'intern' && canAsk ? BUTTON.delenAdvies[language] : BUTTON.delen[language]
}
